import { useState, useCallback, useEffect, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut } from "lucide-react";
import api from "@/lib/api";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export function PdfPreviewDialog({ pdfEndpoint, open, onClose, filename = "documento.pdf" }) {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [loadError, setLoadError] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !pdfEndpoint) {
      setPdfData(null);
      return;
    }
    setLoading(true);
    setLoadError(false);
    setCurrentPage(1);
    api.get(pdfEndpoint, { responseType: "arraybuffer" })
      .then((res) => {
        setPdfData(new Uint8Array(res.data));
      })
      .catch(() => {
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [open, pdfEndpoint]);

  const fileData = useMemo(() => {
    if (!pdfData) return null;
    return { data: pdfData.slice() };
  }, [pdfData]);

  const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setCurrentPage(1);
    setLoadError(false);
  }, []);

  const handleDownload = () => {
    if (!pdfData) return;
    const blob = new Blob([pdfData], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);
    try {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      link.style.cssText = "position:fixed;left:-9999px";
      document.body.appendChild(link);
      link.click();
      // Keep the element and URL alive long enough for the download to start
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 5000);
    } catch {
      window.open(blobUrl, "_blank");
    }
  };

  const handleClose = (isOpen) => {
    if (!isOpen) {
      setNumPages(null);
      setCurrentPage(1);
      setScale(1.2);
      setLoadError(false);
      setPdfData(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col p-0 gap-0" data-testid="pdf-preview-dialog">
        <DialogHeader className="flex flex-row items-center justify-between px-5 py-3 border-b border-zinc-200 shrink-0">
          <DialogTitle className="font-['Outfit'] text-base">Anteprima PDF</DialogTitle>
          <div className="flex items-center gap-2 mr-6">
            {numPages && (
              <>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => setScale(s => Math.max(0.5, s - 0.2))}
                    data-testid="pdf-zoom-out"
                  >
                    <ZoomOut className="w-3.5 h-3.5" />
                  </Button>
                  <span className="text-xs text-zinc-500 w-10 text-center">{Math.round(scale * 100)}%</span>
                  <Button
                    variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => setScale(s => Math.min(2.5, s + 0.2))}
                    data-testid="pdf-zoom-in"
                  >
                    <ZoomIn className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="h-4 w-px bg-zinc-200 mx-1" />
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost" size="icon" className="h-7 w-7"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                    data-testid="pdf-prev-page"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </Button>
                  <span className="text-xs text-zinc-500 min-w-[60px] text-center">{currentPage} / {numPages}</span>
                  <Button
                    variant="ghost" size="icon" className="h-7 w-7"
                    disabled={currentPage >= numPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                    data-testid="pdf-next-page"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </>
            )}
            <div className="h-4 w-px bg-zinc-200 mx-1" />
            <Button
              variant="ghost" size="sm"
              onClick={handleDownload}
              disabled={!pdfData}
              className="h-7 text-xs gap-1"
              data-testid="pdf-download-btn"
            >
              <Download className="w-3.5 h-3.5" /> Scarica
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto bg-zinc-100 flex justify-center p-4" style={{ maxHeight: "calc(90vh - 60px)" }}>
          {loading && (
            <div className="flex justify-center py-20">
              <div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {loadError && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <p className="text-sm text-zinc-500 mb-3">Impossibile caricare il PDF.</p>
            </div>
          )}
          {pdfData && !loading && !loadError && fileData && (
            <Document
              file={fileData}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={() => setLoadError(true)}
              loading={
                <div className="flex justify-center py-20">
                  <div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
                </div>
              }
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            </Document>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
